import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useIsFeedbackOnFeedbackEnabled } from '@modules/feedback/hooks/useIsFeedbackOnFeedbackEnabled';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { FeedbackOnFeedbackActionType } from '@modules/feedback/store/feedbackOnFeedbackActionType';
import {
  getReasonPredictions,
  getSentimentPrediction,
  getTrackedKeywordPredictions,
  getUpdatedEditedRecords,
  toReasonManipulations,
  toSentimentManipulations,
  toTrackedKeywordManipulations,
} from '@modules/feedback/utils';
import { useManipulatePredictionsMutation } from 'generated/graphql';
import { useCallback } from 'react';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { useGetUser } from 'v2/core/services/user';

export const trackFeedbackOnFeedbackEvents =
  createAnalyticsTracker('feedbackOnFeedback');

interface UpdateEditedPredictionsOptions {
  onAfterEditedPredictions?: () => void;
}

export const useUpdateEditedPredictions = (recordID: string) => {
  const dispatch = useFeedbackOnFeedbackStore(({ dispatch }) => dispatch);
  const { showSuccessNotification, showErrorNotification } =
    useNotificationContext();
  const recordInEditMode = useFeedbackOnFeedbackStore(
    ({ recordInEditMode }) => recordInEditMode,
  );
  const {
    tenant: { id: tenantID },
  } = useGetUser();

  const { isFeedbackOnFeedbackEnabled } = useIsFeedbackOnFeedbackEnabled();
  const editedRecords = useFeedbackOnFeedbackStore(
    ({ editedRecords }) => editedRecords,
  );

  const reasonManipulations = toReasonManipulations(
    getReasonPredictions(recordInEditMode),
  );

  const trackedKeywordManipulations = toTrackedKeywordManipulations(
    getTrackedKeywordPredictions(recordInEditMode),
  );

  const sentimentManipulations = toSentimentManipulations(
    getSentimentPrediction(recordInEditMode),
  );

  const manipulationCount =
    reasonManipulations.length +
    trackedKeywordManipulations.length +
    sentimentManipulations.length;

  const [updatePredictions, { loading }] = useManipulatePredictionsMutation({
    variables: {
      request: {
        recordID,
        tenantID,
        manipulations:
          manipulationCount > 0
            ? [
                ...reasonManipulations,
                ...trackedKeywordManipulations,
                ...sentimentManipulations,
              ]
            : undefined,
      },
    },
  });

  const updateEditedPredictions = useCallback(
    async (opts?: UpdateEditedPredictionsOptions) => {
      const onAfterEditedPredictions = opts?.onAfterEditedPredictions;

      if (manipulationCount <= 0 || recordID === '') {
        return;
      }

      try {
        const response = await updatePredictions();

        if (response.data?.manipulatePredictions?.success && recordInEditMode) {
          const existingEditedRecordIndex = editedRecords.findIndex(
            (editedRecord) => editedRecord.id === recordID,
          );
          const payload = getUpdatedEditedRecords({
            editedRecords,
            recordInEditMode,
            existingEditedRecordIndex,
          });
          dispatch({
            type: FeedbackOnFeedbackActionType.SET_EDITED_RECORDS,
            payload,
          });
          dispatch({
            type: FeedbackOnFeedbackActionType.RESET_RECORD_IN_EDIT_MODE,
            payload: {},
          });
          dispatch({
            type: FeedbackOnFeedbackActionType.SET_SHOULD_REFETCH,
            payload: true,
          });
          if (!isFeedbackOnFeedbackEnabled) {
            trackFeedbackOnFeedbackEvents({
              eventName: 'Submit Edited Predictions',
              recordID,
              pageUrl: window.location.href,
              editedPredictions: [
                ...reasonManipulations,
                ...trackedKeywordManipulations,
                ...sentimentManipulations,
              ],
            });
          }
          onAfterEditedPredictions?.();
          if (!isFeedbackOnFeedbackEnabled) {
            showSuccessNotification({
              contentComponent: 'The predictions are edited successfully.',
              props: {},
            });
          }
        }
      } catch {
        showErrorNotification({
          contentComponent: 'Failed to update predictions.',
          props: {},
        });
      }
    },
    [
      dispatch,
      editedRecords,
      isFeedbackOnFeedbackEnabled,
      manipulationCount,
      reasonManipulations,
      recordID,
      recordInEditMode,
      sentimentManipulations,
      showErrorNotification,
      showSuccessNotification,
      trackedKeywordManipulations,
      updatePredictions,
    ],
  );

  return {
    isLoading: loading,
    updateEditedPredictions,
  };
};
